<script setup lang="ts">
    import { computed, onMounted, ref } from 'vue';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import axios from 'axios';
    import { formatAmount } from '@/utils';
    
    const errorMessage = ref('');
    const loading = ref(false);
    const itemsList = ref([] as any[]);
    const selectedItem = ref(null as any);
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const selectedItems = ref([] as any[]);
    const areAllItemsSelected = computed(() => {
        return itemsList.value.length == selectedItems.value.length;
    });

    onMounted(() => {
        doListItems();
    });

    async function doListItems() {
        try {
            loading.value = true;
            errorMessage.value = '';
            const httpResponse = await axios.get(`/backoffice/market-produits/list`);
            itemsList.value = httpResponse.data;
        } catch(error: any) {
            errorMessage.value = error.message;
        } finally {
            loading.value = false;
        }
    }

    async function doDeleteItem(item: any) {
        const confirmation = await confirmationModalRef.value?.open("Produits Market", "Voulez-vous vraiment supprimer l'élément sélectionné ?");
        if (confirmation) {
            try {
                loading.value = true;
                errorMessage.value = '';
                await axios.post(`/backoffice/market-produits/destroy/${item.id}`, {});
                const index = itemsList.value.findIndex(i => i.id == item.id);
                if (index > -1) {
                    const itemsListCloned = itemsList.value;
                    itemsListCloned.splice(index, 1);
                    itemsList.value = itemsListCloned;
                }
            } catch(error: any) {
                errorMessage.value = error.message;
            } finally {
                loading.value = false;
            }
        }
    }

    function doSelectItem(item: any) {
        selectedItem.value = item;
    }

    function doUnselectItem() {
        selectedItem.value = null;
    }

    async function doToggleEtatValideAction(itemsIds: any[]) {
        const confirmation = await confirmationModalRef.value?.open("Produits Market", "Voulez-vous vraiment modifier l'état des éléments sélectionnés ?");
        if (confirmation) {
            try {
                loading.value = true;
                errorMessage.value = '';
                await axios.post(`/backoffice/market-produits/toggle_etat_valide`, { ids: itemsIds });

                itemsIds.forEach(itemId => {
                    const index = itemsList.value.findIndex(i => i.id == itemId);
                    if (index > -1) {
                        itemsList.value[index].actif = itemsList.value[index].actif == 1 ? 0 : 1;
                    }
                });

            } catch(error: any) {
                errorMessage.value = error.message;
            } finally {
                loading.value = false;
            }
        }
    }

    async function doRestoreItem(item: any) {
        const confirmation = await confirmationModalRef.value?.open("Produits Market", "Voulez-vous vraiment restaurer l'élément sélectionné ?");
        if (confirmation) {
            try {
                loading.value = true;
                errorMessage.value = '';
                await axios.post(`/backoffice/market-produits/restore/${item.id}`);
                doListItems();
            } catch (error: any) {
                errorMessage.value = error.message;
            } finally {
                loading.value = false;
            }
        }
    }

    function handleCheckAll(_event: any) {
        const checked = _event.target.checked;
        if (checked) {
            selectedItems.value = itemsList.value.map(i => i.id);
        } else {
            selectedItems.value = [];
        }
    }
</script>

<template>
    <section class="container-full-app bg-grey-50">
        <div class="page-space-30"></div>

        <!--*** Widget *** -->
        <section class="s20">
            <div class="widget shadow-100 pb-2"><!-- widget shadow-100 fill -->
                <!--Card-->
                <div>
                    <div class="container-full-2 my-5">
                        <div class="s7"><h4 class="font-semibold mt-4">Produits Market <span class="text-sm text-grey-500">({{ itemsList.length }})</span></h4></div>
                        <div class="s13 flex justify-content-right">
                            <router-link id="btn-add" to="/app/market/market-produits/form">
                                <button class="btn normal ml-4 justify-content-center btn-filtre">
                                    <img src="/icons/icon-add.svg" class="btn-icon" alt="" />
                                    <span>Créer</span>
                                </button>
                            </router-link>
                            <div class="form-field-floating default sm s4_mobile ml-3">
                                <select class="form-input-select" name="pays" id="pays">
                                    <option value="">Trier</option>
                                    <option value="" selected>Ordre Décroissant</option>
                                    <option value="">Ordre Croissant</option>
                                    <option value="">De A - Z</option>
                                    <option value="">De Z - A</option>
                                </select>
                            </div>
                            <button class="btn normal white2 s3_mobile ml-4 justify-content-center btn-filtre btn-filter">
                                <img src="/icons/icon-filter.svg" class="btn-icon" alt="" />
                                <span>Options</span>
                            </button>
                        </div>
                        <div class="clear-both"></div>
                        <!--Options-->
                        <div class="filter-set-area">
                            <div class="grid-filter6 mt-4 mb-0">
                                <div class="form-field-floating reactive sm">
                                    <input type="date" class="form-input" name="date1" id="date1" placeholder="">
                                    <label for="date1">Date de création &lt;=</label>
                                </div>
                                <div class="form-field-floating reactive sm">
                                    <input type="date" class="form-input" name="date2" id="date2" placeholder="">
                                    <label for="date2">Date de création >=</label>
                                </div>
                                <div class="form-field-floating sm">
                                    <select class="form-input-select" name="pays" id="pays">
                                        <option value="" selected>Tout les etats</option>
                                        <option value="">Actif</option>
                                        <option value="">Inactif</option>
                                    </select>
                                    <label for="pays">Etat <b class="color-danger">*</b></label>
                                </div>
                                <div class="form-field-floating sm">
                                    <select class="form-input-select" name="pays" id="pays">
                                        <option value="" selected>Tout les etats</option>
                                        <option value="">Actif</option>
                                        <option value="">Inactif</option>
                                    </select>
                                    <label for="pays">Etat <b class="color-danger">*</b></label>
                                </div>
                                <button class="btn black normal justify-content-center mx-0 btn-filtre">
                                    <img src="/icons/icon-excel.svg" class="btn-icon" alt="" />
                                    <span>Exporter Excel</span>
                                </button>
                                <button class="btn black normal justify-content-center mx-0 btn-filtre">
                                    <img src="/icons/icon-pdf.svg" class="btn-icon" alt="" />
                                    <span>Exporter PDF</span>
                                </button>
                            </div>
                        </div>
                        <!---->
                    </div>
                    <table class="table separator skin1 selection"><!-- selection skin1 skin2 (bg1 bg2 bg3)  (skin3 rounded)  -->
                        <thead>
                            <tr>
                                <th>
                                    <label class="btn-circle" for="check_all">
                                        <input type="checkbox" id="check_all" :checked="areAllItemsSelected" @change="handleCheckAll" />
                                    </label>
                                </th>
                                <th></th>
                                <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Désignation</th>
                                <th>Catégorie</th>
                                <th>Prix unitaire</th>
                                <th>Stock</th>
                                <th class="flex-right">
                                    <button class="btn-ultra white2 tips display-none"><!-- loading sm tips color2 light white white2 success info warning danger-->
                                        <div class="btn-show">
                                            <img src="/icons/icon-destroy.svg" class="btn-icon icon-neutral" alt="" />
                                            <span>Détruire</span>
                                        </div>
                                        <div class="btn-loading">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                    <button class="btn-ultra white2 tips display-none"><!--loading-->
                                        <div class="btn-show">
                                            <img src="/icons/icon-delete.svg" class="btn-icon" alt="" />
                                            <span>Supprimer</span>
                                        </div>
                                        <div class="btn-loading">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                    <button class="btn-ultra white2 tips display-none"><!--loading-->
                                        <div class="btn-show">
                                            <img src="/icons/icon-restaurer.svg" class="btn-icon icon-neutral" alt="" />
                                            <span>Restaurer</span>
                                        </div>
                                        <div class="btn-loading">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                    <button class="btn-ultra color2 tips display-none">
                                        <div class="btn-show">
                                            <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                            <span>Actif</span>
                                        </div>
                                        <div class="btn-loading">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-if="itemsList.length == 0">
                                <tr>
                                    <td colspan="7">
                                        <div class="text-center text-grey-700">Aucun élément trouvé</div>
                                    </td>
                                </tr>
                            </template>
                            <template v-for="(item, index) in itemsList" :key="'item_' + index">
                                <tr class="text-grey-700 over-effect over-effect-scale"><!-- over-effect active-->
                                    <td class="selection-item">
                                        <label class="btn-circle" :for="'item_check_' + index">
                                            <input type="checkbox" name="selectedItems[]" :id="'item_check_' + index" :value="item.id" v-model="selectedItems" />
                                        </label>
                                    </td>
                                    <td>
                                        <div class="square-lg px-3 py-3" style="background-color: rgb(var(--color-success-rgb), 0.1);">
                                            <img :src="item.image_url ? item.image_url : '/icons/icon-securite-role.svg'" width="100%" />
                                        </div>
                                    </td>
                                    <td><h6>{{ item.nom }}</h6></td>
                                    <td><h6>{{ item.categories_noms }}</h6></td>
                                    <td><h6 class="text-center">{{ formatAmount(item.montant) }}</h6></td>
                                    <td><h6 class="text-center">{{ item.quantite }}</h6></td>
                                    <td class="flex-right mt-1">
                                        <custom-button :classesList="'btn-ultra white2 tips'" :id="'btn-delete-' + item.id" @click="doDeleteItem(item)" v-if="item.deleted_at == null">
                                            <img src="/icons/icon-delete.svg" class="btn-icon" alt="" />
                                            <span>Supprimer</span>
                                        </custom-button>
                                        <custom-button :classesList="'btn-ultra white2 tips'" :id="'btn-restore-' + item.id" @click="doRestoreItem(item)" v-if="item.deleted_at != null">
                                            <img src="/icons/icon-restaurer.svg" class="btn-icon" alt="" />
                                            <span>Restaurer</span>
                                        </custom-button>
                                        <custom-button :classesList="'btn-ultra color2 tips'" @click="doToggleEtatValideAction([item.id])"  v-if="item.deleted_at == null && item.actif == 1">
                                            <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                            <span>Actif</span>
                                        </custom-button>
                                        <custom-button :classesList="'btn-ultra color2 tips'" @click="doToggleEtatValideAction([item.id])" v-if="item.deleted_at == null && item.actif == 0">
                                            <img src="/icons/icon-inactif.svg" class="btn-icon" alt="" />
                                            <span>Inactif</span>
                                        </custom-button>
                                        <router-link :to="'/app/market/market-produits/form/' + item.id">
                                            <button class="btn" :id="'btn-edit-' + item.id"><!-- sm tips color2 light white white2 success info warning danger -->
                                                <img src="/icons/icon-edit.svg" class="btn-icon" alt="" />
                                                <span>Editer</span>
                                            </button>
                                        </router-link>
                                    </td>
                                </tr>
                            </template>
                        </tbody>
                    </table>
                </div>
                <div class="clear-both h-30"></div>
            </div>
        </section>
        
        <div class="clear-both h-30"></div>
        <confirmation-modal ref="confirmationModalRef"></confirmation-modal>
    </section>
</template>
